import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { authApi } from '@/services/api';
import { useAuthStore } from '@/store/useAuthStore';
import { SUPPORT_EMAIL } from '@/lib/legalCopy';
import { getContentLocale } from '@/lib/contentLocale';
import { APP_NAME } from '@/lib/brand';

type LegalSection = 'cgu' | 'privacy' | 'delete-account';

export function LegalPage() {
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const locale = getContentLocale();
  const active: LegalSection =
    section === 'privacy' || section === 'delete-account' ? section : 'cgu';
  const { isAuthenticated, isGuestSession, logout } = useAuthStore();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const t = locale === 'en'
    ? {
        back: 'Back',
        cgu: 'Terms of use',
        privacy: 'Privacy',
        delete: 'Delete account',
        titleCgu: 'Terms of use (CGU)',
        titlePrivacy: 'Privacy policy',
        titleDelete: 'Delete your account',
      }
    : {
        back: 'Retour',
        cgu: 'CGU',
        privacy: 'Confidentialité',
        delete: 'Supprimer le compte',
        titleCgu: "Conditions générales d'utilisation",
        titlePrivacy: 'Politique de confidentialité',
        titleDelete: 'Supprimer votre compte',
      };

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!confirm) return;
    setDeleting(true);
    try {
      await authApi.deleteAccount(isGuestSession ? undefined : password);
      logout();
      toast.success(locale === 'en' ? 'Your account has been deleted.' : 'Votre compte a été supprimé.');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      toast.error(Array.isArray(msg) ? msg.join(', ') : msg || (locale === 'en' ? 'Unable to delete account' : 'Impossible de supprimer le compte'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-app px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-xs text-fg-faint hover:text-fg-subtle transition-colors mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> {t.back}
        </button>

        <div className="flex flex-wrap gap-2 mb-6">
          {([
            ['cgu', t.cgu],
            ['privacy', t.privacy],
            ['delete-account', t.delete],
          ] as const).map(([id, label]) => (
            <Link
              key={id}
              to={id === 'cgu' ? '/legal/cgu' : `/legal/${id}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                active === id
                  ? 'bg-green-500/15 text-green-400 border border-green-500/25'
                  : 'bg-overlay-1 text-fg-subtle border border-line'
              }`}
            >
              {label}
            </Link>
          ))}
        </div>

        <article className="prose-legal space-y-4 text-sm text-fg-subtle leading-relaxed">
          {active === 'cgu' && (
            <>
              <h1 className="text-2xl font-bold text-fg">{t.titleCgu}</h1>
              <p className="text-xs text-fg-faint">
                {APP_NAME} · exam-copilot.com · {locale === 'en' ? 'Last update' : 'Dernière mise à jour'} : 24/08/2026
              </p>
              <h2 className="text-base font-semibold text-fg pt-2">1. {locale === 'en' ? 'Service' : 'Objet du service'}</h2>
              <p>
                {locale === 'en'
                  ? `${APP_NAME} is a training platform that provides practice questions, quizzes and exam simulations to help users prepare for professional certifications and academic exams.`
                  : `${APP_NAME} est une plateforme d’entraînement proposant des questions de pratique, des quiz et des simulations afin d’aider les utilisateurs à préparer des certifications professionnelles et des examens.`}
              </p>
              <h2 className="text-base font-semibold text-fg pt-2">2. {locale === 'en' ? 'Unofficial practice — no affiliation' : 'Questions de préparation non officielles — absence d’affiliation'}</h2>
              <p>
                {locale === 'en'
                  ? 'All practice questions, quizzes and simulations offered on the Service are unofficial training materials created for educational purposes. They are not official exams.'
                  : 'Toutes les questions, quiz et simulations proposés sur le Service sont des supports de préparation non officiels, créés à des fins pédagogiques. Ils ne constituent pas des examens officiels.'}
              </p>
              <p>
                {locale === 'en'
                  ? `${APP_NAME} is not affiliated with, endorsed by, sponsored by, or otherwise connected to Amazon Web Services (AWS), Amazon.com, Inc., Microsoft Corporation, Microsoft Azure, Google LLC, Google Cloud, Cisco, CompTIA, PMI, Pearson VUE, or any other certifying body, trademark owner or examination provider. Names of certifications, products and organizations are used solely to identify the subject of the practice material. They remain the property of their respective owners.`
                  : `${APP_NAME} n’est ni affilié, ni cautionné, ni sponsorisé par Amazon Web Services (AWS), Amazon.com, Inc., Microsoft Corporation, Microsoft Azure, Google LLC, Google Cloud, Cisco, CompTIA, PMI, Pearson VUE, ni par aucun autre organisme certificateur, titulaire de marques ou prestataire d’examens. Les noms de certifications, produits et organisations sont utilisés uniquement pour identifier le sujet du matériel d’entraînement. Ils restent la propriété de leurs titulaires respectifs.`}
              </p>
              <p>
                {locale === 'en'
                  ? 'Using the Service does not guarantee that you will pass any official certification or exam.'
                  : 'L’utilisation du Service ne garantit en aucun cas la réussite d’une certification ou d’un examen officiel.'}
              </p>
              <h2 className="text-base font-semibold text-fg pt-2">3. {locale === 'en' ? 'Account' : 'Compte'}</h2>
              <p>
                {locale === 'en'
                  ? 'You may create an account to save progress. You can delete your account at any time from Profile or from this page. Deletion is irreversible and removes or anonymizes associated personal data as described in the privacy policy.'
                  : 'Vous pouvez créer un compte pour sauvegarder votre progression. Vous pouvez supprimer votre compte à tout moment depuis le Profil ou depuis cette page. La suppression est irréversible et entraîne l’effacement ou l’anonymisation des données personnelles associées, conformément à la politique de confidentialité.'}
              </p>
              <h2 className="text-base font-semibold text-fg pt-2">4. {locale === 'en' ? 'Subscriptions and payment' : 'Abonnements et paiement'}</h2>
              <p>
                {locale === 'en'
                  ? 'Paid subscriptions are sold only on the Exam Copilot website, from a web browser (FlexPay and other web payment methods). They are not sold inside the iOS or Android store apps, which do not offer in-app purchases. Content unlocked by a web subscription remains accessible in the mobile app after you sign in with the same account.'
                  : 'Les abonnements payants s’achètent uniquement sur le site Exam Copilot, depuis un navigateur web (FlexPay et autres moyens de paiement web). Ils ne sont pas vendus dans les applications des stores iOS ou Android, qui ne proposent pas d’achats intégrés. Le contenu débloqué via un abonnement web reste accessible dans l’application mobile après connexion avec le même compte.'}
              </p>
              <h2 className="text-base font-semibold text-fg pt-2">5. {locale === 'en' ? 'Acceptable use' : 'Usage acceptable'}</h2>
              <p>
                {locale === 'en'
                  ? 'You must not copy, redistribute or sell the question bank, attempt to reverse-engineer the Service, or use it to cheat on an official exam.'
                  : 'Il est interdit de copier, redistribuer ou vendre la banque de questions, de tenter de reverse-engineer le Service, ou de l’utiliser pour tricher à un examen officiel.'}
              </p>
              <h2 className="text-base font-semibold text-fg pt-2">6. {locale === 'en' ? 'Contact' : 'Contact'}</h2>
              <p>
                {locale === 'en' ? 'Questions about these terms:' : 'Questions relatives aux présentes conditions :'}{' '}
                <a className="text-green-400" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              </p>
            </>
          )}

          {active === 'privacy' && (
            <>
              <h1 className="text-2xl font-bold text-fg">{t.titlePrivacy}</h1>
              <p>
                {locale === 'en'
                  ? `We process your name, email, authentication data, quiz progress, gamification stats and, when you subscribe on the website, payment references needed to activate your plan. Data is used to operate the Service, secure accounts, and fulfill subscriptions.`
                  : `Nous traitons votre nom, e-mail, données d’authentification, progression aux quiz, statistiques de gamification et, lorsque vous vous abonnez sur le site, les références de paiement nécessaires à l’activation de l’abonnement. Ces données servent à faire fonctionner le Service, sécuriser les comptes et exécuter les abonnements.`}
              </p>
              <p>
                {locale === 'en'
                  ? `You may access, correct or delete your data by updating your profile or deleting your account. Payment records may be anonymized and retained as required for accounting and fraud prevention.`
                  : `Vous pouvez accéder à vos données, les rectifier ou les supprimer en mettant à jour votre profil ou en supprimant votre compte. Les traces de paiement peuvent être anonymisées et conservées autant que nécessaire à la comptabilité et à la prévention de la fraude.`}
              </p>
              <p>
                {locale === 'en' ? 'Contact:' : 'Contact :'}{' '}
                <a className="text-green-400" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              </p>
            </>
          )}

          {active === 'delete-account' && (
            <>
              <h1 className="text-2xl font-bold text-fg">{t.titleDelete}</h1>
              <p>
                {locale === 'en'
                  ? 'You can delete your Exam Copilot account from the app (Profile) or here. This removes your profile, progress and related personal data. The action cannot be undone.'
                  : 'Vous pouvez supprimer votre compte Exam Copilot depuis l’application (Profil) ou ici. Cela efface votre profil, votre progression et les données personnelles associées. L’action est irréversible.'}
              </p>
              {!isAuthenticated ? (
                <p>
                  {locale === 'en'
                    ? `Sign in on this page to delete your account, or email ${SUPPORT_EMAIL} from the address linked to your account.`
                    : `Connectez-vous sur cette page pour supprimer votre compte, ou écrivez à ${SUPPORT_EMAIL} depuis l’adresse associée au compte.`}
                </p>
              ) : (
                <form onSubmit={handleDelete} className="mt-4 space-y-3 bg-overlay-1 border border-line rounded-2xl p-5">
                  {!isGuestSession && (
                    <input
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={locale === 'en' ? 'Current password' : 'Mot de passe actuel'}
                      className="w-full px-4 py-3 bg-app border border-line text-fg rounded-xl text-sm"
                    />
                  )}
                  <label className="flex items-start gap-2 text-xs text-fg-subtle">
                    <input
                      type="checkbox"
                      checked={confirm}
                      onChange={(e) => setConfirm(e.target.checked)}
                      className="mt-0.5"
                    />
                    {locale === 'en'
                      ? 'I understand that deletion is permanent.'
                      : 'Je comprends que la suppression est définitive.'}
                  </label>
                  <button
                    type="submit"
                    disabled={deleting || !confirm}
                    className="w-full py-3 bg-red-500/15 border border-red-500/25 text-red-400 rounded-xl text-sm font-semibold disabled:opacity-50"
                  >
                    {deleting
                      ? (locale === 'en' ? 'Deleting…' : 'Suppression…')
                      : (locale === 'en' ? 'Delete my account' : 'Supprimer mon compte')}
                  </button>
                </form>
              )}
            </>
          )}
        </article>
      </div>
    </div>
  );
}
